import React, { useMemo, useCallback } from "react";
import MainPageDialog from "../dialog";
import { useDeleteGroupMutation, useAddGroupMutation } from "../../services/triageServices";
import ActionDialog from "../actionDialog";
import { InputField } from "../reactHookFormFields/inputField";
import { useForm } from "react-hook-form";
import { ReactSelectField } from "../reactHookFormFields/selectField";

const GroupRow = React.memo(({ group, isEven, onDelete }) => {
  const formattedDate = useMemo(() => {
    return new Date(group.updated).toLocaleDateString();
  }, [group.updated]);

  return (
    <tr className={`${isEven ? 'bg-gray-100' : 'bg-white'} hover:bg-gray-100 transition-colors`}>
      <td className="py-2 px-3 text-left text-sm">
        <div className="flex items-center gap-2">
          <a href="#" className="text-blue-500 hover:underline text-sm">
            {group.title}
          </a>
          <span className="text-gray-500 text-xs">
            ({group.user_array?.length || 0})
          </span>
        </div>
      </td>
      <td className="py-2 px-3 text-gray-600 text-left text-sm">
        {formattedDate}
      </td>
      <td className="py-2 px-3 text-center">
        <button className="text-red-500 font-bold text-lg" onClick={() => onDelete(group.id)}>
          ×
        </button>
      </td>
    </tr>
  );
});

function ManageGroups({ isOpen = false, setIsOpen = () => {}, taskGroupsData = [], allProviders = [] }) {
  if(!isOpen) return null;
  
  const [deleteGroup, { isLoading: isDeleteGroupLoading }] = useDeleteGroupMutation();
  const [addGroup, { isLoading: isAddGroupLoading }] = useAddGroupMutation();

  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [selectedGroup, setSelectedGroup] = React.useState(null);

  const { control, handleSubmit, formState: { errors }, setValue } = useForm();

  const handleDeleteGroup = async (id) => {
    try {
      await deleteGroup({ id: String(id) });
      setShowDeleteDialog(false);
      setSelectedGroup(null);
    } catch (error) {
      console.error("Failed to delete group:", error);
    }
  };

  const handleOpenDeleteDialog = (group) => {
    setSelectedGroup(group);
    setShowDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setSelectedGroup(null);
  };

  const footerList = useMemo(() => [
    { title: "Close", type: "cancel", action: () => setIsOpen(false) },
  ], [setIsOpen]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleReset = useCallback(() => {
    setValue("title", "");
    setValue("user_id", []);
  }, [setValue]);

  const onSubmit = useCallback(async (data) => {
    try {
      const payload = {
        title: data.title,
        user_id: Array.isArray(data.user_id) ? data.user_id.map(u => u.value).join(",") : ""
      };
      await addGroup(payload);
      handleReset();
    } catch (error) {
      console.error("Failed to add group:", error);
    }
  }, [addGroup, handleReset]);

  return (
    <>
      <MainPageDialog
        title="Manage Groups"
        onCancelHandler={handleCancel}
        maxHeight={true}
        footer={true}
        footerList={footerList}
      >
        <div className="flex p-4 gap-4">
          <div className="flex flex-1 flex-col max-h-[calc(100vh-200px)] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-ethizo_primary text-white">
                  <th className="py-2 px-3 text-left text-sm font-medium">Title</th>
                  <th className="py-2 px-3 text-left text-sm font-medium">Last Updated</th>
                  <th className="py-2 px-3 text-center text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {taskGroupsData?.map((group, index) => (
                  <GroupRow key={group.id} group={group} isEven={index % 2 === 0} onDelete={() => handleOpenDeleteDialog(group)} />
                ))}
              </tbody>
            </table>
          </div>
          <form className="flex flex-1 flex-col gap-4">
            <InputField name="title" control={control} title="Group Name" rules={{ required: { value: true, message: "Group Name is required" } }} />
            <ReactSelectField name="user_id" control={control} title="Members" rules={{ required: { value: true, message: "Members is required" } }} optionData={allProviders} menuPlacement="bottom" isMulti={true} />
            <div className="flex items-end justify-start gap-2">
              <button 
                type="submit" 
                className="bg-ethizo_primary text-white px-4 py-2 rounded-md hover:bg-ethizo_primary/90 flex items-center gap-2"
                onClick={handleSubmit(onSubmit)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit(onSubmit)();
                  }
                }}
                aria-label="Add"
                title="Add"
                disabled={isAddGroupLoading}
              >
                <p>{isAddGroupLoading ? "Adding" : "Add"}</p>
              </button>
              {
                !isAddGroupLoading && (
                  <button 
                    type="button" 
                    className="bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-500/90 flex items-center gap-2"
                    onClick={handleReset}
                    onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        handleReset();
                    }
                    }}
                    aria-label="Reset"
                    title="Reset"
                    disabled={isAddGroupLoading}
                    >
                    <p>Reset</p>
                    </button>
                )
              }
            </div>
          </form>
        </div>
      </MainPageDialog>
      <ActionDialog
        showModal={showDeleteDialog}
        headerTitle="Delete Group"
        setShowModal={setShowDeleteDialog}
        title={selectedGroup ? `Do you want to delete the (${selectedGroup.title}) group?` : ''}
        onCancelHandler={handleCloseDeleteDialog}
        onConfirmHandler={() => handleDeleteGroup(selectedGroup?.id)}
        cancelButtonText="No"
        confirmButtonText={isDeleteGroupLoading ? "Deleting" : "Yes"}
      />
    </>
  );
}

export default React.memo(ManageGroups);
